import { logger } from '@/shared/utils';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
   IconAudioMute,
   IconMic,
   IconVideoMute,
   IconVideo,
   IconNewWindow,
   IconUnpin,
   IconPin,
   ZoomIn,
   ZoomOut,
   IconPinnedFilled,
   IconPoorNetwork,
} from '@/assets/images';
import { UserProfile, Username } from '@/features/users';
import { ExternalBadge } from '@/features/invite/ui';
import LocalVideoPreview from '../CallConnectionScreen/LocalVideoPreview';
import { CallMediaStore } from '../../../utils/CallMediaStore';
import styles from './VideoTile.module.scss';
import { CALLKIT_CALLSTATUS, CALL_PARTICIPANT_CONNECTION_QUALITY } from '../../../constants';
import { useLanguage } from '@/localization/useLanguage';
import { isEnabled } from '@/features/calls/subFeatureRegistry';
import { getCallStatusLabel } from '../../../utils/helper';
import { AppIcon, CustomButton, Loader } from '@/components';
import { stopScreen } from '@/features/calls/services/callService';

export default function VideoTile({
   participant,
   isPinned = false,
   isActiveSpeaker = false,
   isPrimary = false,
   isSidebar = false,
   handlePin,
   onOverflowClick,
}) {
   const { t } = useLanguage();
   const isOverflow = participant?.isOverflow;
   const [zoomLevel, setZoomLevel] = useState(0);
   const [isPortrait, setIsPortrait] = useState(false);
   const [hasVideoTrack, setHasVideoTrack] = useState(false);
   const videoRef = useRef(null);
   const audioRef = useRef(null);
   const busyAudioRef = useRef(null);
   const isPinFeature = isEnabled('participantPin');
   const isZoomFeature = isEnabled('screenShareZoom');
   const isScreenSharing = participant?.isScreenSharing;
   const usersInfo = useSelector(state => state.userInfoData?.users || {});
   const [isStreamStuck, setIsStreamStuck] = useState(false);
   const [isOnline, setIsOnline] = useState(navigator.onLine);
   const [isTabHidden, setIsTabHidden] = useState(document.hidden);

   useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      const handleVisibility = () => setIsTabHidden(document.hidden);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleVisibility);
      return () => {
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
         document.removeEventListener('visibilitychange', handleVisibility);
      };
   }, []);

   useEffect(() => {
      if (participant?.isLocal) {
         if (!busyAudioRef.current) {
            busyAudioRef.current = new Audio('/sounds/busySignal.mp3');
            busyAudioRef.current.loop = true;
         }

         if (participant?.callStatus === CALLKIT_CALLSTATUS.RECONNECTING) {
            busyAudioRef.current.play().catch(err => {
               logger.warn('Busy sound autoplay failed:', err);
            });
         } else if (busyAudioRef.current) {
            busyAudioRef.current.pause();
            busyAudioRef.current.currentTime = 0;
         }
      }

      return () => {
         if (busyAudioRef.current) {
            busyAudioRef.current.pause();
            busyAudioRef.current.currentTime = 0;
         }
      };
   }, [participant?.callStatus, participant?.isLocal]);

   const [videoReady, setVideoReady] = useState(false);

   useEffect(() => {
      if (participant?.isVideoMuted) {
         setVideoReady(false);
      }
   }, [participant?.isVideoMuted]);

   useEffect(() => {
      let track = null;
      if (!isOverflow && participant?.id && !participant?.isVideoMuted) {
         track = CallMediaStore.getTrack(participant.id, 'video');
      }

      if (track && videoRef.current) {
         if (typeof track.attach === 'function') {
            track.attach(videoRef.current);
         } else if (track.mediaStreamTrack || track instanceof MediaStreamTrack) {
            const rawTrack = track.mediaStreamTrack || track;
            videoRef.current.srcObject = new MediaStream([rawTrack]);
         }
         setHasVideoTrack(true);
      } else {
         setHasVideoTrack(false);
         setVideoReady(false);
      }

      return () => {
         if (track && videoRef.current) {
            if (typeof track.detach === 'function') {
               track.detach(videoRef.current);
            } else {
               videoRef.current.srcObject = null;
            }
         }
      };
   }, [participant?.isVideoMuted, isOverflow, participant?.id, participant?.trackUpdated, participant?.callStatus]);

   useEffect(() => {
      // Screen shares legitimately run at < 6 fps on static content — never treat as stuck
      if (participant?.isLocal || participant?.isVideoMuted || !videoReady || !videoRef.current || isScreenSharing) {
         setIsStreamStuck(false);
         return;
      }

      let lastFrameCount = null; // null = baseline not yet established
      let lastCheckTime = Date.now();
      let stuckCount = 0;
      const STUCK_THRESHOLD = 6; // require 6 consecutive low-fps ticks before showing loader

      const checkStuck = setInterval(() => {
         if (!videoRef.current) return;

         const quality = videoRef.current.getVideoPlaybackQuality?.();
         if (!quality) return;

         const currentFrameCount = quality.totalVideoFrames;
         const now = Date.now();
         const elapsed = (now - lastCheckTime) / 1000;

         if (elapsed < 1) return;

         if (lastFrameCount === null) {
            // First tick: establish baseline, don't judge yet
            lastFrameCount = currentFrameCount;
            lastCheckTime = now;
            return;
         }

         // totalVideoFrames stays 0 on Safari/Mac for remote WebRTC tracks — API unsupported, skip
         if (currentFrameCount === 0) {
            lastFrameCount = currentFrameCount;
            lastCheckTime = now;
            return;
         }

         const fps = (currentFrameCount - lastFrameCount) / elapsed;

         if (fps < 6) {
            stuckCount++;
            if (stuckCount >= STUCK_THRESHOLD) setIsStreamStuck(true);
         } else {
            stuckCount = 0;
            setIsStreamStuck(false);
         }

         lastFrameCount = currentFrameCount;
         lastCheckTime = now;
      }, 1000);

      return () => clearInterval(checkStuck);
   }, [videoReady, participant?.isVideoMuted, participant?.isLocal, isScreenSharing]);

   useEffect(() => {
      if (!participant?.isLocal && !participant?.isAudioMuted && !isOverflow && participant?.id) {
         const track = CallMediaStore.getTrack(participant.id, 'audio');
         if (track && audioRef.current) {
            const mediaTrack = track.mediaStreamTrack || track;
            const currentStream = audioRef.current.srcObject;
            const currentTrack = currentStream?.getAudioTracks()[0];
            if (currentTrack?.id === mediaTrack.id) {
               return;
            }
            const stream = new MediaStream([mediaTrack]);
            audioRef.current.srcObject = stream;
            audioRef.current.play().catch(e => {
               if (e.name !== 'AbortError') {
                  logger.error('Error playing audio:', e);
               }
            });
         }
      }
   }, [
      participant?.isLocal,
      participant?.isAudioMuted,
      isOverflow,
      participant?.id,
      participant?.trackUpdated,
      participant?.callStatus,
   ]);

   const getStatusText = () => {
      // Normalize case before comparing — some participant sources (e.g. the initial
      // subscribeCall participants list) can hand back a differently-cased status
      // (e.g. "Connected" instead of "CONNECTED"), which silently failed this exact
      // check and fell through to rendering the raw value as a "Connected" label.
      const status = participant?.callStatus?.toUpperCase?.();
      if (
         (participant?.isLocal && status !== CALLKIT_CALLSTATUS.RECONNECTING) ||
         !status ||
         status === CALLKIT_CALLSTATUS.CONNECTED
      )
         return '';

      return getCallStatusLabel(status, t);
   };

   const statusText = getStatusText();

   const isConnectionLost = participant?.connectionQuality === CALL_PARTICIPANT_CONNECTION_QUALITY.LOST;
   const isConnectionPoor = participant?.connectionQuality === CALL_PARTICIPANT_CONNECTION_QUALITY.POOR;
   const hasPoorConnection = !isOverflow && (isConnectionLost || isConnectionPoor);

   const showVideoContainer = !participant?.isVideoMuted && !isOverflow;

   // Fires for ALL reconnection cases regardless of video mute state (audio calls included).
   const isActuallyReconnecting =
      participant?.callStatus === CALLKIT_CALLSTATUS.RECONNECTING ||
      isStreamStuck ||
      (participant?.isLocal && !isOnline) ||
      (participant?.isLocal && isTabHidden && !participant?.isVideoMuted);

   return (
      <div
         className={[
            styles.tile,
            participant?.isAudioMuted ? styles.muted : '',
            isActiveSpeaker ? styles.active : '',
            isPrimary ? styles.primaryTile : '',
            isSidebar ? styles.sidebarTile : '',
            isActuallyReconnecting ? styles.reconnecting : '',
         ].join(' ')}
      >
         {/* 1. Main Media Area (Live or Fallback) */}
         <div className={styles.mediaArea}>
            {showVideoContainer ? (
               <div className={styles.live}>
                  {participant?.isLocal && !participant?.isScreenSharing ? (
                     <LocalVideoPreview isVideoMuted={participant?.isVideoMuted} isPaused={isActuallyReconnecting} />
                  ) : (
                     <>
                        <video
                           ref={videoRef}
                           autoPlay
                           playsInline
                           muted
                           className={[
                              isPortrait ? styles.isPortrait : '',
                              isScreenSharing ? styles.isScreenSharing : '',
                           ].join(' ')}
                           style={{
                              transform: zoomLevel > 0 ? `scale(${1 + zoomLevel * 0.2})` : 'none',
                              opacity: videoReady ? 1 : 0,
                           }}
                           onLoadedMetadata={() => {
                              if (videoRef.current) {
                                 setIsPortrait(videoRef.current.videoHeight > videoRef.current.videoWidth);
                              }
                           }}
                           onPlaying={() => setVideoReady(true)}
                           onPause={() => {
                              // Don't hide the frozen frame during reconnection
                              if (!isActuallyReconnecting) setVideoReady(false);
                           }}
                        >
                           <track kind="captions" />
                        </video>
                        {(!hasVideoTrack || !videoReady) && (
                           <div className={styles.fallback} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                              {isScreenSharing ? (
                                 <div className={styles.screenShareLoader}>
                                    <Loader size={48} color="white" thickness={4} />
                                    <span>{t('calls.LOADING_SCREEN_SHARE')}</span>
                                 </div>
                              ) : (
                                 <UserProfile
                                    userId={participant?.id || participant?.userId}
                                    className={[styles.avatar, isPrimary ? styles.primaryAvatar : ''].join(' ')}
                                 />
                              )}
                           </div>
                        )}
                     </>
                  )}
               </div>
            ) : (
               <div className={styles.fallback}>
                  {isOverflow ? (
                     <div className={`${styles.overflowWrapper}`}>
                        <CustomButton
                           as="div"
                           className={`${styles.avatarGroup} ct-cursor`}
                           onClick={() => onOverflowClick?.()}
                           aria-label={'View All'}
                        >
                           {participant.remainingParticipants?.slice(0, 3).map((p, idx) => (
                              <div key={p.id} className={styles.stackedItem}>
                                 {idx === 2 && participant.remainingParticipants.length > 3 ? (
                                    <div className={`${styles.avatar} ${styles.countAvatar}`}>
                                       +{participant.remainingParticipants.length - 2}
                                    </div>
                                 ) : (
                                    <UserProfile userId={p.id} className={`chat-avatar-text ${styles.avatar}`} />
                                 )}
                              </div>
                           ))}
                        </CustomButton>
                        <div className={`${styles.name}`}>
                           <span className={styles.overflowText}>
                              {(() => {
                                 const list = participant.remainingParticipants || [];
                                 const names = list.map(item => usersInfo[item.id]?.nickName || item.name || 'User');
                                 if (names.length === 0) return '';
                                 if (names.length === 1) return names[0];
                                 if (names.length === 2) return `${names[0]} & ${names[1]}`;
                                 if (names.length === 3) return `${names[0]}, ${names[1]} & ${names[2]}`;
                                 return `${names[0]}, ${names[1]} & ${names.length - 2} others`;
                              })()}
                           </span>
                        </div>
                     </div>
                  ) : (
                     <UserProfile
                        userId={participant?.id || participant?.userId}
                        className={[styles.avatar, isPrimary ? styles.primaryAvatar : ''].join(' ')}
                     />
                  )}
               </div>
            )}
         </div>

         {/* 2. Reconnection overlay + loader — only when video is active.
               Audio-only tiles (video muted) use the status label for feedback. */}
         {isActuallyReconnecting && (
            <>
               <div className={styles.reconnectOverlay} />
               <div className={styles.reconnectLoader}>
                  <div className={styles.reconnectSpinner} />
               </div>
            </>
         )}

         {/* 3. Other overlays and Labels (Root level of tile) */}
         {isScreenSharing && (
            <>
               <button className={styles.presentingName}>
                  <AppIcon name="close" size={16} icon={<IconNewWindow />} />
                  <span className="ct-flex ct-aiCenter ct-gap4">
                     {!isSidebar && (
                        <span className={styles.nameEllipsis}>
                           {participant?.name || (participant?.isLocal ? t('calls.YOU') : t('calls.USER'))}
                        </span>
                     )}
                     <span>{t('calls.IS_PRESENTING_SCREEN')}</span>
                  </span>
               </button>

               {participant?.isLocal && (
                  <div className={styles.screenshareOverlay}>
                     {!isSidebar && (
                        <div className={styles.screenshareOverlayContent}>
                           <h4>{t('calls.YOU_ARE_PRESENTING')}</h4>
                           <p>{t('calls.TO_AVOID_AN_INFINITY_MIRROR')}</p>
                           <button className={styles.stopScreenShareBtn} onClick={() => stopScreen(() => {})}>
                              {t('calls.STOP_PRESENTING')}
                           </button>
                        </div>
                     )}
                  </div>
               )}
            </>
         )}

         {/* This container holds the name and status at the bottom of the tile */}
         {!isOverflow && (
            <div className={styles.label}>
               <div className={styles.nameLabelWrapper}>
                  {isPinned && <AppIcon size="xs" icon={<IconPinnedFilled />} className={styles.pinnedIconIndicator} />}
                  <Username
                     userId={participant?.id || participant?.userId}
                     name={participant?.name || participant?.id}
                     isLocal={participant?.isLocal}
                     style={{ color: '#fff' }}
                  />
                  {!participant?.isLocal && !isSidebar && (
                     <ExternalBadge
                        isExternalUser={usersInfo[participant?.id || participant?.userId]?.isExternal}
                        className="external-badge--compact"
                     />
                  )}
               </div>
               {statusText && (
                  <div className={styles.statusContainer}>
                     <span className={styles.status}>{statusText}</span>
                     {participant?.callStatus !== CALLKIT_CALLSTATUS.CONNECTED &&
                        participant?.callStatus !== CALLKIT_CALLSTATUS.RECONNECTING && (
                           <div className={styles.statusLoader}>
                              <div className={styles.bar}></div>
                              <div className={styles.bar}></div>
                              <div className={styles.bar}></div>
                              <div className={styles.bar}></div>
                           </div>
                        )}
                  </div>
               )}
            </div>
         )}

         <div className={styles.topRight}>
            {!isOverflow && isScreenSharing && isZoomFeature && (
               <div className={styles.zoomControls}>
                  {zoomLevel > 0 && (
                     <button
                        className={`${styles.zoomBtn} ct-cursor`}
                        onClick={e => {
                           e.stopPropagation();
                           setZoomLevel(prev => Math.max(0, prev - 1));
                        }}
                        title={t('common.ZOOM_OUT')}
                     >
                        <AppIcon size="sm" icon={<ZoomOut />} />
                     </button>
                  )}
                  {zoomLevel < 5 && (
                     <button
                        className={`${styles.zoomBtn} ct-cursor`}
                        onClick={e => {
                           e.stopPropagation();
                           setZoomLevel(prev => Math.min(5, prev + 1));
                        }}
                        title={t('common.ZOOM_IN')}
                     >
                        <AppIcon size="sm" icon={<ZoomIn />} />
                     </button>
                  )}
               </div>
            )}
            {!isOverflow && !isScreenSharing && (
               <div className={styles.statusBadge}>
                  <AppIcon size="sm" icon={participant?.isVideoMuted ? <IconVideoMute /> : <IconVideo />} />
               </div>
            )}
            {!isOverflow && !isScreenSharing && (
               <div className={styles.statusBadge}>
                  <AppIcon size="sm" icon={participant?.isAudioMuted ? <IconAudioMute /> : <IconMic />} />
               </div>
            )}
         </div>

         {!isOverflow && isPinFeature && (
            <button
               className={[styles.pinBtn, isPinned ? styles.isPinned : ''].join(' ')}
               onClick={e => {
                  e.stopPropagation();
                  handlePin(participant.id);
               }}
            >
               <AppIcon size="md" icon={isPinned ? <IconUnpin /> : <IconPin />} />
            </button>
         )}

         {!isOverflow && !isScreenSharing && (!participant?.isAudioMuted || hasPoorConnection) && (
            <div className={styles.topLeft}>
               {!participant?.isAudioMuted && (
                  <div className={styles.speakingIndicatorBadge}>
                     <div className={styles.bars}>
                        <div className={styles.bar}></div>
                        <div className={styles.bar}></div>
                        <div className={styles.bar}></div>
                     </div>
                  </div>
               )}
               {hasPoorConnection && (
                  <div
                     className={[styles.networkBadge, isConnectionLost ? styles.networkLost : ''].join(' ')}
                  >
                     <AppIcon size="sm" icon={<IconPoorNetwork />} />
                  </div>
               )}
            </div>
         )}

         {!participant?.isLocal && !participant?.isAudioMuted && !isOverflow && !isScreenSharing && (
            <audio ref={audioRef} autoPlay>
               <track kind="captions" />
            </audio>
         )}
      </div>
   );
}

VideoTile.propTypes = {
   participant: PropTypes.shape({
      id: PropTypes.string,
      userId: PropTypes.string,
      name: PropTypes.string,
      isLocal: PropTypes.bool,
      isOverflow: PropTypes.bool,
      isVideoMuted: PropTypes.bool,
      isAudioMuted: PropTypes.bool,
      connectionQuality: PropTypes.string,
      callStatus: PropTypes.string,
      trackUpdated: PropTypes.number,
      isScreenSharing: PropTypes.bool,
      remainingCount: PropTypes.number,
      remainingParticipants: PropTypes.arrayOf(
         PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
         }),
      ),
   }),
   isPinned: PropTypes.bool,
   isActiveSpeaker: PropTypes.bool,
   isPrimary: PropTypes.bool,
   isSidebar: PropTypes.bool,
   handlePin: PropTypes.func,
   onOverflowClick: PropTypes.func,
};
